import React from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

const SUPPORTED = ["fr", "de", "en", "it", "pt", "es"];
const STORAGE_KEY = "i18nextLng";
const GEO_KEY = "geoLangChecked";
const RESET_FLAG_KEY = "langResetV2";

// One-time reset: clears any previously persisted language so historical
// visitors (who may be stuck on /de from the old default) re-run detection
// and land on FR (or their geo-IP language).
function runOneTimeReset() {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(RESET_FLAG_KEY)) return;
    window.localStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(GEO_KEY);
    window.localStorage.setItem(RESET_FLAG_KEY, "1");
  } catch {}
}

// Background geo-IP check via cached edge function (server-side ipapi.co + 24h cache).
async function backgroundGeoCheck(): Promise<string | null> {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(GEO_KEY)) return null;

    // Fetch the client's real public IP first (Supabase edge sees the gateway IP, not the user's).
    let clientIp: string | null = null;
    try {
      const ipRes = await fetch("https://api.ipify.org?format=json");
      if (ipRes.ok) {
        const ipJson = await ipRes.json();
        clientIp = ipJson?.ip || null;
      }
    } catch {}

    const { supabase } = await import("@/integrations/supabase/client");
    const { data, error } = await supabase.functions.invoke("geo-lang", {
      body: clientIp ? { ip: clientIp } : {},
    });
    window.sessionStorage.setItem(GEO_KEY, "1");
    if (error || !data?.lang) return null;
    const geoLang = data.lang as string;
    if (!SUPPORTED.includes(geoLang)) return null;
    return geoLang;
  } catch {
    return null;
  }
}

function detectLang(): string {
  if (typeof window === "undefined") return "fr";

  // 1. Previously chosen language wins
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const cleaned = saved.replace(/^auto:/, "");
      const short = cleaned.toLowerCase().split("-")[0];
      if (SUPPORTED.includes(short)) return short;
    }
  } catch {}

  // 2. Default to French. Geo-IP (server-side) may override asynchronously
  //    via backgroundGeoCheck() based on the visitor's actual country.
  //    We intentionally ignore navigator.language to avoid serving German
  //    just because the browser UI is in German.
  return "fr";
}

interface Props {
  /** Path appended after the language prefix, e.g. "/auth" or "" */
  suffix?: string;
}

const LangRedirect: React.FC<Props> = ({ suffix = "" }) => {
  const location = useLocation();
  const params = useParams();
  const [lang, setLang] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;

    const resolveLang = async () => {
      runOneTimeReset();
      const detectedLang = detectLang();

      try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (!saved || saved.startsWith("auto:")) {
          const geoLang = await backgroundGeoCheck();
          const finalLang = geoLang && SUPPORTED.includes(geoLang) ? geoLang : detectedLang;
          window.localStorage.setItem(STORAGE_KEY, `auto:${finalLang}`);
          if (active) setLang(finalLang);
          return;
        }
      } catch {}

      if (active) setLang(detectedLang);
    };

    resolveLang();
    return () => {
      active = false;
    };
  }, []);

  if (!lang) return null;

  // Substitute :param tokens in the suffix with current route params
  // (so /dossier/:id correctly redirects to /{lang}/dossier/{id}).
  const resolvedSuffix = suffix.replace(/:([a-zA-Z_]+)/g, (_, key) => {
    return params[key] ? encodeURIComponent(params[key] as string) : "";
  });

  return <Navigate to={`/${lang}${resolvedSuffix}${location.search}${location.hash}`} replace />;
};

export default LangRedirect;